import { fromJS, Map } from 'immutable';
import * as Sentry from '@sentry/react';
import Big from 'big.js';

import { getBillingPrice, getTier, isSubscribed, shapeQuoteForApp } from '../utils/billing';
import { isEbayInvalidMarketplace } from '../utils/shops';
import { createController } from '../utils/controller';
import { getLegacyDiscountedPrice } from '../utils/legacyBilling';
import { navigateTo } from '../utils/navigation';
import { getSize } from '../utils/iterable/getSize';
import { isAfter } from '../utils/time';
import { isTrue } from '../utils/bool';
import api from '../utils/api';

import { BILLING_V3_TIMESTAMP, LEGACY_TIER_MAP, MULTI_SHOP_DISCOUNT, PLAN } from '../constants/billing';
import { BILLING_V3_PLUS_PREFERRED } from '../constants/user';
import { MESSAGE, NOTIFICATION } from '../constants/notifications';
import { EMPTY, SEPARATOR } from '../constants';
import { BETA_CHANNELS } from '../constants/features';
import { EBAY, SHOPIFY } from '../constants/channels';
import { LEGACY_TIER } from '../constants/legacyBilling';
import { STATUSES } from '../constants/shops';
import { ENDPOINT } from '../constants/api';
import { MODALS } from '../constants/modal';

import actions from '../actions';

const customerController = createController();
const invoicesController = createController();
const shopsController = createController();
const manageController = createController();

function abort(controller) {
  if (controller.isActive()) {
    controller.stop();
  }
}

function abortAll() {
  [
    customerController,
    invoicesController,
    shopsController,
    manageController,
  ].forEach(abort);
}

function* cleanUp(state) {
  abortAll();
  return state.set('billing', EMPTY.MAP);
}

function* getCustomer(state) {
  abort(customerController);

  yield function effect(dispatch) {
    const { signal } = customerController.start();
    api.billing
      .getCustomer({ signal })
      .then(
        (response) => dispatch(actions.billing.getCustomer.fulfilled(response)),
        (error) => dispatch(actions.billing.getCustomer.rejected({ error, signal })),
      );
  };

  return state;
}

function* getCustomerFulfilled(state, response) {
  abort(customerController);

  let billing = state.get('billing') || EMPTY.MAP;

  if (billing.has('paymentError')) {
    billing = billing.delete('paymentError');
  }

  billing = billing.set('customer', fromJS(response));

  return state.set('billing', billing);
}

function* getCustomerRejected(state, { error, signal }) {
  if (signal.aborted) return state;

  abort(customerController);

  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_DATA,
      })
    );
  };

  return state;
}

function* getInvoices(state) {
  function reduceShopIds(result, shopId) {
    const shop = state.getIn(['shops', 'byId', shopId]);

    if (shop.get('isVelaOnlyShop')) return result;

    return result.push(shopId);
  }

  const shopIds = state.getIn(['shops', 'options']).reduce(reduceShopIds, EMPTY.LIST);

  if (getSize(shopIds) > 0) {
    return state.setIn(['billing', 'invoices'], Map({ shopIds, selected: shopIds.get(0) }));
  }

  return state;
}

function* getShops(state) {
  abort(shopsController);

  const empty = Map({ byId: EMPTY.MAP, ids: EMPTY.LIST });
  const subscriptions = state.getIn(['user', 'subscriptions']);

  if (!getSize(subscriptions)) return state.setIn(['billing', 'shops'], empty);

  const userShops = state.get('shops');
  const { signal } = shopsController.start();

  function addCount(count = 0) {
    return count + 1;
  }

  function reduceShopIds(result, shopId) {
    const subscription = state.getIn(['user', 'subscriptions', shopId]);
    const shop = userShops.getIn(['byId', shopId]);
    const isVelaOnlyShop = shop.get('isVelaOnlyShop');

    if (!subscription || isVelaOnlyShop) return result;

    const channel = shop.get('channel');
    const plan = subscription.get('plan');
    const db = shop.get('db');
    const shopName = shop.get('name');

    if (plan === PLAN.FREE || BETA_CHANNELS.has(channel)) {
      result.actions.push(
        actions.billing.getShop.fulfilled({
          shop: {
            channel,
            db,
            plan: PLAN.FREE,
            shopBadge: BETA_CHANNELS.has(channel) ? 'Beta' : 'Free',
            shopId,
            shopName,
          },
        })
      );

      result.shops = result.shops.set('ids', result.shops.get('ids').push(shopId));
      return result;
    }

    if (subscription.get('frozen')) {
      const futurePlan = PLAN.PLUS;
      const frozen = true;

      result.actions.push(
        actions.billing.getShop({
          channel,
          db,
          frozen,
          futurePlan,
          plan,
          shopId,
          shopName,
          signal,
        })
      );

      result.shops = result.shops.update('connected', addCount).updateIn(['subscriptions', plan], addCount);
      result.shops = result.shops.set('ids', result.shops.get('ids').push(shopId));

      return result;
    }

    switch (channel) {
      case EBAY: {
        if (isEbayInvalidMarketplace(shop.get('syncStatus'))) {
          return result;
        }

        break;
      }

      default: {
        break;
      }
    }

    const planName = plan?.split(SEPARATOR.DOT)[0];

    switch (planName) {
      case PLAN.TRIAL: {
        const discount = subscription.get('discountPercent') || 0;
        const trialRemaining = subscription.get('trialRemaining') || 0;
        const trialExpired = trialRemaining <= 0;
        const futurePlan = PLAN.PLUS;

        result.actions.push(
          actions.billing.getShop({
            channel,
            db,
            discount,
            dueDate: null,
            futurePlan,
            plan,
            shopId,
            shopName,
            signal,
            trialExpired,
          })
        );

        result.shops = result.shops.update('connected', addCount);
        result.shops = result.shops.set('ids', result.shops.get('ids').push(shopId));
        break;
      }

      case PLAN.PLUS:
      case PLAN.LITE: {
        const canceled = !!subscription.get('cancelAtPeriodEnd');
        const tierChange = !!subscription.get('tierChangeDetected');
        const subscriptionId = subscription.get('subscriptionId');
        const discount = subscription.get('discountPercent') || 0;
        const dueDate = subscription.get('dueDate');
        const tier = plan.split(SEPARATOR.DOT)[1];
        const price = getBillingPrice({ discount, plan: planName, tier });
        const undiscountedPrice = discount > 0
          ? getBillingPrice({ plan: planName, tier })
          : 0;

        const futurePlan = PLAN.PLUS;

        result.actions.push(
          actions.billing.getShop({
            canceled,
            channel,
            db,
            discount,
            dueDate,
            futurePlan,
            plan: planName,
            price,
            shopId,
            shopName,
            signal,
            subscriptionId,
            tier,
            tierChange,
            undiscountedPrice,
          })
        );

        if (!canceled) {
          result.shops = result.shops
            .update('connected', addCount)
            .updateIn(['subscriptions', planName], addCount);
        }

        result.shops = result.shops.set('ids', result.shops.get('ids').push(shopId));
        break;
      }

      case LEGACY_TIER.T_0:
      case LEGACY_TIER.T_251:
      case LEGACY_TIER.T_501:
      case LEGACY_TIER.T_1001:
      case LEGACY_TIER.T_2501:
      case LEGACY_TIER.T_5001:
      case LEGACY_TIER.T_7501:
      case LEGACY_TIER.T_10001:
      case LEGACY_TIER.T_20001:
      case LEGACY_TIER.T_30001:
      case LEGACY_TIER.T_40001:
      case LEGACY_TIER.T_50001:
      case LEGACY_TIER.T_60001:
      case LEGACY_TIER.T_70001: {
        const canceled = !!subscription.get('cancelAtPeriodEnd');
        const tierChange = !!subscription.get('tierChangeDetected');
        const subscriptionId = subscription.get('subscriptionId');
        const discount = subscription.get('discountPercent') || 0;
        const dueDate = subscription.get('dueDate');
        const tier = LEGACY_TIER_MAP[planName];
        const futurePlan = PLAN.PLUS;

        let price = subscription.get('price') || 0;
        let undiscountedPrice = 0;

        if (discount > 0) {
          undiscountedPrice = price;
          price = getLegacyDiscountedPrice({ discount, price });
        }

        result.actions.push(
          actions.billing.getShop({
            canceled,
            channel,
            db,
            discount,
            dueDate,
            futurePlan,
            plan: PLAN.VIP,
            price,
            shopId,
            shopName,
            signal,
            subscriptionId,
            tier,
            tierChange,
            undiscountedPrice,
          })
        );

        if (!canceled) {
          result.shops = result.shops.update('connected', addCount);
        }

        result.shops = result.shops.set('ids', result.shops.get('ids').push(shopId));
        break;
      }

      default: {
        Sentry.captureException('Unexpected shop plan', {
          extra: {
            db,
            shopId,
            subscription: subscription.toJS(),
          },
        });

        break;
      }
    }

    return result;
  }

  const { actions: actionsToDispatch, shops } = userShops
    .get('options')
    .reduce(reduceShopIds, { actions: [], shops: empty });

  if (getSize(actionsToDispatch)) {
    yield function effect(dispatch) {
      actionsToDispatch.forEach(dispatch);
    };
  }

  return state.setIn(['billing', 'shops'], shops);
}

function* getShop(state, { signal, ...shop }) {
  yield function effect(dispatch) {
    api.billing
      .getSubscription({ db: shop.db, shopId: shop.shopId, signal })
      .then(
        (response) => {
          const { lite: { product_count: listings }} = response;
          dispatch(
            actions.billing.getShop.fulfilled({
              ...shop,
              listings: listings || 0,
              quote: shapeQuoteForApp(response),
            })
          );
        },
        (error) => dispatch(actions.billing.getShop.rejected({ error, shop, signal }))
      );
  };

  return state;
}

function* getShopFulfilled(state, shop) {
  let shops = state.getIn(['billing', 'shops']);
  const { canceled, dueDate, listings, plan, shopId } = shop;

  switch (plan) {
    case PLAN.LITE:
    case PLAN.PLUS: {
      const tier = getTier({ listings });

      shop.discount = shops.get('connected') > 1 ? MULTI_SHOP_DISCOUNT : 0;

      if (tier !== shop.tier) {
        shop.tier = tier;
        shop.price = getBillingPrice({ discount: shop.discount, plan, tier: shop.tier });
        shop.undiscountedPrice = shop.discount > 0
          ? getBillingPrice({ plan, tier: shop.tier })
          : 0;
      }

      if (!canceled) {
        shops = shops.set('total', new Big(shops.get('total', 0)).plus(shop.price).toNumber());
      }

      break;
    }

    case PLAN.TRIAL: {
      shop.tier = getTier({ listings });
      shop.discount = shops.get('connected') > 1 ? MULTI_SHOP_DISCOUNT : 0;
      shop.price = getBillingPrice({ discount: shop.discount, plan: shop.futurePlan, tier: shop.tier });
      shop.undiscountedPrice = shop.discount > 0
        ? getBillingPrice({ plan: shop.futurePlan, tier: shop.tier })
        : 0;

      break;
    }

    case PLAN.VIP: {
      if (isAfter(new Date(dueDate), BILLING_V3_TIMESTAMP)) {
        const futurePlan = isTrue(state.getIn(['user', `${BILLING_V3_PLUS_PREFERRED}${shopId}`]))
          ? PLAN.PLUS
          : PLAN.LITE;

        shop.tier = getTier({ listings });
        shop.discount = shops.get('connected') > 1 ? MULTI_SHOP_DISCOUNT : 0;
        shop.price = getBillingPrice({ discount: shop.discount, plan: futurePlan, tier: shop.tier });
        shop.undiscountedPrice = shop.discount > 0
          ? getBillingPrice({ plan: futurePlan, tier: shop.tier })
          : 0;
      }

      if (!canceled) {
        shops = shops.set('total', new Big(shops.get('total', 0)).plus(shop.price).toNumber());
      }

      break;
    }

    default: {
      break;
    }
  }

  return state.setIn(['billing', 'shops'], shops.setIn(['byId', shopId], Map(shop)));
}

function* getShopRejected(state, { error, shop, signal }) {
  if (signal.aborted) return state;

  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_DATA,
      })
    );
  };

  return state.setIn(['billing', 'shops', 'byId', shop.shopId], Map(shop));
}

function* manageShop(state, { type, ...rest } = {}) {
  abort(manageController);

  switch (type) {
    case MODALS.BILLING.MANAGE: {
      const { shopId, quote } = rest;

      if (quote) {
        const { channel } = rest;
        return state.setIn(['billing', 'manage'], Map({ channel, shop: Map(rest), shopId, type }));
      }

      const shop = state.getIn(['shops', 'byId', shopId]);

      if (!shop) return state;

      const channel = shop.get('channel');
      const db = shop.get('db');

      if (shop.get('syncStatus') === STATUSES.TOKEN_REJECTED) {
        if (isSubscribed(state.getIn(['user', 'subscriptions', shopId]))) {
          rest.reconnect = ENDPOINT.CONNECT_CHANNEL[channel];
        } else {
          navigateTo(`/${shopId}`);
          return state;
        }
      }

      yield function effect(dispatch) {
        const { signal } = manageController.start();
        api.billing
          .getSubscription({ db, shopId, signal })
          .then(
            (response) => dispatch(actions.billing.manageShop.fulfilled({ response, shop: rest })),
            (error) => dispatch(actions.billing.manageShop.rejected({ error, signal }))
          );
      };

      return state.setIn(['billing', 'manage'], Map({ channel, shopId, type, ...rest }));
    }

    case MODALS.BILLING.SUCCESS: {
      const { action, shopId } = rest;
      const shop = state.getIn(['shops', 'byId', shopId]);

      if (!shop) return state;

      const channel = shop.get('channel');
      const shopName = shop.get('name');

      return state.setIn(['billing', 'manage'], Map({ action, channel, shopId, shopName, type }));
    }

    default: {
      return state.deleteIn(['billing', 'manage']);
    }
  }
}

function* manageShopFulfilled(state, { response, shop: source }) {
  abort(manageController);

  const { shopId } = source;
  const { lite: { product_count: listings, sync_status: syncStatus }} = response;
  const shop = state.getIn(['shops', 'byId', shopId]);
  const subscription = state.getIn(['user', 'subscriptions', shopId], EMPTY.MAP);

  if (syncStatus === STATUSES.TOKEN_REJECTED && shop.get('syncStatus') !== syncStatus) {
    yield function effect(dispatch) {
      dispatch(actions.shops.getShops());
      navigateTo(`/${shopId}`);
    };

    return state.setIn(['shops', 'byId', shopId, 'syncStatus'], syncStatus);
  }

  function updateData(data) {
    const result = {
      channel: shop.get('channel'),
      db: shop.get('db'),
      listings,
      quote: shapeQuoteForApp(response),
      shopId,
      shopName: shop.get('name'),
    };

    if (subscription.get('frozen')) {
      result.discount = subscription.get('discountPercent') || 0;
      result.futurePlan = PLAN.PLUS;
      result.frozen = true;
      result.plan = subscription.get('plan');
      result.tier = getTier({ listings });
      result.price = getBillingPrice(result);
      result.undiscountedPrice = result.discount > 0
        ? getBillingPrice({ plan: result.plan, tier: result.tier })
        : 0;

      return data.set('shop', Map(result));
    }

    const plan = subscription.get('plan', PLAN.TRIAL).split(SEPARATOR.DOT)[0];

    switch (plan) {
      case PLAN.LITE:
      case PLAN.PLUS: {
        result.discount = subscription.get('discountPercent') || 0;
        result.futurePlan = PLAN.PLUS;
        result.plan = plan;
        result.tier = getTier({ listings });
        result.price = getBillingPrice(result);
        result.dueDate = subscription.get('dueDate');
        result.canceled = !!subscription.get('cancelAtPeriodEnd');
        result.tierChange = !!subscription.get('tierChangeDetected');
        result.subscriptionId = subscription.get('subscriptionId');
        result.undiscountedPrice = result.discount > 0
          ? getBillingPrice({ plan: result.plan, tier: result.tier })
          : 0;

        break;
      }

      case PLAN.TRIAL: {
        result.plan = plan;
        result.futurePlan = PLAN.PLUS;
        result.trialExpired = (subscription.get('trialRemaining') || 0) <= 0;
        result.tier = getTier({ listings });
        result.futurePlan = PLAN.PLUS;
        result.discount = result.quote.getIn([result.futurePlan, 'discount']);
        result.price = getBillingPrice({ discount: result.discount, plan: result.futurePlan, tier: result.tier });
        result.undiscountedPrice = result.discount > 0
          ? getBillingPrice({ plan: result.futurePlan, tier: result.tier })
          : 0;

        break;
      }

      case LEGACY_TIER.T_0:
      case LEGACY_TIER.T_251:
      case LEGACY_TIER.T_501:
      case LEGACY_TIER.T_1001:
      case LEGACY_TIER.T_2501:
      case LEGACY_TIER.T_5001:
      case LEGACY_TIER.T_7501:
      case LEGACY_TIER.T_10001:
      case LEGACY_TIER.T_20001:
      case LEGACY_TIER.T_30001:
      case LEGACY_TIER.T_40001:
      case LEGACY_TIER.T_50001:
      case LEGACY_TIER.T_60001:
      case LEGACY_TIER.T_70001: {
        result.plan = PLAN.VIP;
        result.dueDate = subscription.get('dueDate');
        result.canceled = !!subscription.get('cancelAtPeriodEnd');
        result.tierChange = !!subscription.get('tierChangeDetected');
        result.subscriptionId = subscription.get('subscriptionId');
        result.futurePlan = PLAN.PLUS;

        if (isAfter(new Date(result.dueDate), BILLING_V3_TIMESTAMP)) {
          const futurePlan = isTrue(state.getIn(['user', `${BILLING_V3_PLUS_PREFERRED}${shopId}`]))
            ? PLAN.PLUS
            : PLAN.LITE;

          result.tier = getTier({ listings });
          result.discount = result.quote.getIn([futurePlan, 'discount']);
          result.price = getBillingPrice({ discount: result.discount, plan: futurePlan, tier: result.tier });
          result.undiscountedPrice = result.discount > 0
            ? getBillingPrice({ plan: futurePlan, tier: result.tier })
            : 0;
        } else {
          result.discount = subscription.get('discountPercent') || 0;
          result.price = subscription.get('price') || 0;
          result.undiscountedPrice = 0;

          if (result.discount > 0) {
            result.undiscountedPrice = result.price;
            result.price = getLegacyDiscountedPrice({ discount: result.discount, price: result.price });
          }
        }

        break;
      }

      default: {
        break;
      }
    }

    return data.set('shop', Map(result));
  }

  return state.updateIn(['billing', 'manage'], updateData);
}

function* manageShopRejected(state, { error, signal }) {
  if (signal.aborted) return state;

  abort(manageController);

  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_DATA,
      })
    );
  };

  return state.deleteIn(['billing', 'manage']);
}

function* setData(state, { path, value }) {
  const fullPath = Array.isArray(path)
    ? ['billing', ...path]
    : ['billing', path];

  if (value) return state.setIn(fullPath, value);

  let updated = state.deleteIn(fullPath);

  fullPath.pop();

  while (fullPath.length > 1) {
    if (!getSize(updated.getIn(fullPath))) {
      updated = updated.deleteIn(fullPath);
    }

    fullPath.pop();
  }

  return updated;
}

function* getShopInvoices(state, { shopId }) {
  abort(invoicesController);

  const shop = state.getIn(['shops', 'byId', shopId]);

  if (shop.get('channel') !== SHOPIFY) {
    yield function effect(dispatch) {
      const db = shop.get('db');
      const { signal } = invoicesController.start();

      api.billing
        .getInvoices({ db, shopId, signal })
        .then(
          (response) => dispatch(actions.billing.getShopInvoices.fulfilled({ response, shopId })),
          (error) => dispatch(actions.billing.getShopInvoices.rejected({ error, signal }))
        );
    };
  }

  return state;
}

function* getShopInvoicesFulfilled(state, { response, shopId }) {
  abort(invoicesController);

  if (getSize(response.invoices) > 0) {
    return state.setIn(['billing', 'invoices', 'byShopId', shopId], fromJS(response.invoices));
  }

  let shopIds = state.getIn(['billing', 'invoices', 'shopIds']);
  const index = shopIds.indexOf(shopId);
  shopIds = shopIds.splice(index, 1);

  const selected = shopIds.get(Math.max(0, Math.min(getSize(shopIds) - 1, index)));

  return state
    .setIn(['billing', 'invoices', 'shopIds'], shopIds)
    .setIn(['billing', 'invoices', 'selected'], selected);
}

function* getShopInvoicesRejected(state, { error, signal }) {
  if (signal.aborted) return state;

  abort(invoicesController);

  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_INVOICES,
      })
    );
  };

  return state;
}

function* togglePaymentMethodModal(state, paymentError ) {
  let billing = state.get('billing') || EMPTY.MAP;
  if (paymentError) {
    billing = billing.setIn(['paymentError'], paymentError);
  }

  billing = billing.getIn(['showPaymentMethodModal'])
    ? billing.deleteIn(['showPaymentMethodModal'])
    : billing.setIn(['showPaymentMethodModal'], true);

  return state.set('billing', billing);
}

const billing = {
  [actions.billing.getCustomer]: getCustomer,
  [actions.billing.getCustomer.fulfilled]: getCustomerFulfilled,
  [actions.billing.getCustomer.rejected]: getCustomerRejected,
  [actions.billing.cleanUp]: cleanUp,
  [actions.billing.getInvoices]: getInvoices,
  [actions.billing.getShops]: getShops,
  [actions.billing.getShop]: getShop,
  [actions.billing.getShop.fulfilled]: getShopFulfilled,
  [actions.billing.getShop.rejected]: getShopRejected,
  [actions.billing.getShopInvoices]: getShopInvoices,
  [actions.billing.getShopInvoices.fulfilled]: getShopInvoicesFulfilled,
  [actions.billing.getShopInvoices.rejected]: getShopInvoicesRejected,
  [actions.billing.manageShop]: manageShop,
  [actions.billing.manageShop.fulfilled]: manageShopFulfilled,
  [actions.billing.manageShop.rejected]: manageShopRejected,
  [actions.billing.setData]: setData,
  [actions.billing.togglePaymentMethodModal]: togglePaymentMethodModal,
};

export default billing;
